import React from 'react';
import { Box } from '@mui/material';
import { Chip } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import moment from 'moment';
import { WorkspacesOutlined } from '@mui-symbols-material/w300';
import { HeroListCard, HeroListRow } from '../dashboardHero/heroPrimitives';
import { SkeletonListRows, fadeInSx } from '../dashboardHero/HeroSkeletons';
import { AssetInstallationsList } from '../../graphql/queries/assetInstallationsList';

interface Props {
    serialNumber: string;
    assetLocationId: string;
    compact?: boolean;
}

/**
 * Lists every location where this serial number is installed (the same physical asset in
 * multiple places / over time). The current installation is tagged and not navigable.
 * Only rendered when there is more than the current installation.
 */
const AssetInstallationsWidget = ({ serialNumber, assetLocationId, compact }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const limit = compact ? 3 : 5;

    const { data, loading, error } = useQuery(AssetInstallationsList, {
        variables: { filter: { serialNumber: { equals: serialNumber } } },
        skip: !serialNumber,
    });

    const total = _.get(data, 'searchAssets.total', 0) ?? 0;
    const results: any[] = _.get(data, 'searchAssets.searchAssetResults', []) ?? [];

    if (error) return null;
    // Nothing new to show beyond the current installation (or still loading the first response).
    if (!loading && total <= 1) return null;

    const rows = results
        .map((r) => r.assetLocation)
        .filter(Boolean)
        .slice(0, limit);

    return (
        <HeroListCard
            title={t('Installations')}
            icon={<WorkspacesOutlined />}
            count={total}
            dense={compact}
            viewAll={t('View all installations')}
            onViewAll={() => navigate(`/assets?query=${encodeURIComponent(serialNumber)}&fields=SERIAL_NUMBER`)}
        >
            {loading && !data ? (
                <SkeletonListRows rows={compact ? 2 : 3} />
            ) : (
                <Box sx={fadeInSx}>
                    {rows.map((al) => {
                        const isCurrent = al.id === assetLocationId;
                        const location = _.get(al, 'installedBaseNode.title') || al.title;
                        const meta = [_.get(al, 'owner.name'), al.installationDate ? moment(al.installationDate).format('DD.MM.YYYY') : null]
                            .filter(Boolean)
                            .join(' · ');
                        return (
                            <HeroListRow
                                key={al.id}
                                icon={<WorkspacesOutlined />}
                                title={location}
                                meta={meta || undefined}
                                action={isCurrent ? (
                                    <Chip size="sm" variant="soft" color="neutral">
                                        {t('current')}
                                    </Chip>
                                ) : undefined}
                                onClick={isCurrent ? undefined : () => navigate(`/assets/${al.id}/dashboard`)}
                            />
                        );
                    })}
                </Box>
            )}
        </HeroListCard>
    );
};

export default AssetInstallationsWidget;
